import { useState, useEffect, useRef } from 'react';

// ─── LoginSheet ───────────────────────────────────────────────────────────────
// Bottom sheet matching PaywallModal pattern.
// Not dismissible — user must log in or register to use the app.
//
// Props:
//   onLogin       — async (email, password) => void — throws on error; throws with .code for EMAIL_NOT_VERIFIED
//   onRegister    — async (email, password) => { requiresVerification, email } | throws on error
//   onGoogleLogin — async (idToken) => void — throws on error
export default function LoginSheet({ onLogin, onRegister, onGoogleLogin, onClose, initialMode }) {
  const [mode, setMode] = useState(initialMode || 'login'); // 'login' | 'register' | 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const emailRef = useRef(null);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (mode !== 'verify') emailRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mode]);

  useEffect(() => {
    let cancelled = false;

    async function handleCredential(response) {
      setError(null);
      setGoogleLoading(true);
      try {
        await onGoogleLogin(response.credential);
      } catch (err) {
        setError(err.message || 'Google sign-in failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    }

    async function setup() {
      try {
        const hostname = window.location.hostname;
        if (hostname.endsWith('.repl.run')) return;

        const res = await fetch('/auth/config');
        const data = await res.json();
        const clientId = data?.data?.googleClientId;
        if (!clientId || cancelled) return;

        const waitForGoogle = () => new Promise((resolve) => {
          if (window.google?.accounts?.id) return resolve();
          const interval = setInterval(() => {
            if (window.google?.accounts?.id) { clearInterval(interval); resolve(); }
          }, 100);
        });
        await waitForGoogle();
        if (cancelled || !googleBtnRef.current) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
        });
        setGoogleAvailable(true);
      } catch {
        // Google sign-in is optional — silently skip if config/script unavailable
      }
    }

    setup();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function switchMode(next) {
    setMode(next);
    setError(null);
    setPassword('');
    setConfirmPassword('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await onLogin(email, password);
      } else {
        const result = await onRegister(email, password);
        if (result?.requiresVerification) {
          setPendingEmail(result.email || email);
          setMode('verify');
          return;
        }
      }
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setPendingEmail(err.email || email);
        setMode('verify');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResendState('sending');
    try {
      await fetch('/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail }),
      });
      setResendState('sent');
    } catch {
      setResendState('idle');
    }
  }

  const isLogin = mode === 'login';

  if (mode === 'verify') {
    return (
      <>
        <div className="login-backdrop" aria-hidden="true" onClick={onClose || undefined} style={onClose ? { cursor: 'default' } : undefined} />
        <div className="login-sheet" role="dialog" aria-modal="true" aria-labelledby="login-title">
          <div className="login-handle-row">
            <div className="login-handle" aria-hidden="true" />
            {onClose && (
              <button className="login-close-btn" onClick={onClose} aria-label="Close">✕</button>
            )}
          </div>
          <div className="login-content">
            <div className="login-mascot-wrap" aria-hidden="true">
              <img src="/media/logo-mascot.png" className="login-mascot-img" alt="" />
            </div>
            <div style={{ fontSize: 36, marginBottom: 4 }}>📬</div>
            <h2 className="login-title" id="login-title">Check your inbox</h2>
            <p className="login-subtitle" style={{ marginBottom: 8 }}>
              We've sent a verification link to
            </p>
            <p style={{ fontWeight: 500, fontSize: 14, color: 'var(--color-text-heading)', marginBottom: 24, textAlign: 'center' }}>
              {pendingEmail}
            </p>
            <p className="login-subtitle" style={{ marginBottom: 28 }}>
              Click the link in the email to verify your address and start creating listings.
              The link expires in 24 hours.
            </p>

            {resendState === 'sent' ? (
              <p style={{ fontSize: 13, color: '#4a7c59', textAlign: 'center', marginBottom: 12 }}>
                ✓ Verification email resent
              </p>
            ) : (
              <button
                className="login-cta"
                style={{ background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-heading)', marginBottom: 8 }}
                onClick={handleResend}
                disabled={resendState === 'sending'}
              >
                {resendState === 'sending'
                  ? <span className="login-spinner login-spinner--dark" aria-label="Sending" />
                  : 'Resend verification email'}
              </button>
            )}

            <p className="login-toggle">
              <button className="login-toggle-btn" onClick={() => switchMode('login')}>
                Back to sign in
              </button>
            </p>
          </div>
        </div>
        <style>{loginSheetStyles}</style>
      </>
    );
  }

  return (
    <>
      {/* ── Backdrop */}
      <div className="login-backdrop" aria-hidden="true" onClick={onClose || undefined} style={onClose ? { cursor: 'default' } : undefined} />

      {/* ── Sheet */}
      <div
        className="login-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-title"
      >
        {/* Drag handle + optional close */}
        <div className="login-handle-row">
          <div className="login-handle" aria-hidden="true" />
          {onClose && (
            <button className="login-close-btn" onClick={onClose} aria-label="Close">✕</button>
          )}
        </div>

        {/* ── Content */}
        <div className="login-content">
          {/* Mascot character */}
          <div className="login-mascot-wrap" aria-hidden="true">
            <img src="/media/logo-mascot.png" className="login-mascot-img" alt="" />
          </div>

          <h2 className="login-title" id="login-title">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="login-subtitle">
            {isLogin
              ? 'Sign in to start creating listings with sold4you.'
              : 'Start with 3 free listings — no card required.'}
          </p>

          {/* ── Google Sign-in */}
          <div className="login-google-wrap" style={{ display: googleAvailable ? 'flex' : 'none' }}>
            <div ref={googleBtnRef} className="login-google-btn" />
            {googleLoading && <span className="login-spinner login-spinner--dark" aria-label="Signing in with Google" />}
          </div>
          {googleAvailable && <div className="login-divider"><span>or</span></div>}

          {/* ── Form */}
          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="auth-email">Email</label>
              <input
                ref={emailRef}
                id="auth-email"
                className="login-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="auth-password">Password</label>
              <input
                id="auth-password"
                className="login-input"
                type="password"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                placeholder={isLogin ? '••••••••' : 'Min. 8 characters'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            {!isLogin && (
              <div className="login-field">
                <label className="login-label" htmlFor="auth-confirm">Confirm password</label>
                <input
                  id="auth-confirm"
                  className="login-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="login-error" role="alert">
                <span aria-hidden="true">⚠</span> {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="login-cta"
              disabled={loading || !email || !password || (!isLogin && !confirmPassword)}
            >
              {loading ? (
                <span className="login-spinner" aria-label="Loading" />
              ) : (
                <>
                  <span>{isLogin ? 'Sign in' : 'Create account'}</span>
                  <span className="login-cta__arrow" aria-hidden="true">→</span>
                </>
              )}
            </button>
          </form>

          {/* Mode toggle */}
          <p className="login-toggle">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            {' '}
            <button
              className="login-toggle-btn"
              onClick={() => switchMode(isLogin ? 'register' : 'login')}
              disabled={loading}
            >
              {isLogin ? 'Sign up free' : 'Sign in'}
            </button>
          </p>

          <p className="login-trust">Secure · No spam · Cancel anytime</p>
          <p className="login-privacy">
            By signing up you agree to our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="login-privacy-link">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>

      <style>{loginSheetStyles}</style>
    </>
  );
}

const loginSheetStyles = `
  .login-backdrop {
    position: fixed; inset: 0;
    background: rgba(26, 26, 26, 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 100;
    animation: login-fade-in 0.25s ease;
  }

  @keyframes login-fade-in { from { opacity: 0; } to { opacity: 1; } }

  .login-sheet {
    position: fixed; bottom: 0; left: 0; right: 0;
    z-index: 101;
    background: var(--color-bg-card);
    border-radius: 14px 14px 0 0;
    border-top: 0.5px solid var(--color-border);
    padding: 12px 24px calc(36px + env(safe-area-inset-bottom, 0px));
    max-width: 480px; margin: 0 auto;
    animation: login-slide-up 0.32s cubic-bezier(0.34, 1.2, 0.64, 1);
  }

  @keyframes login-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }

  .login-handle-row {
    display: flex; align-items: center; justify-content: center;
    position: relative; margin-bottom: 20px;
  }
  .login-handle {
    width: 36px; height: 4px; border-radius: 9999px;
    background: var(--color-border-dashed);
  }
  .login-close-btn {
    position: absolute; right: 0; top: 50%; transform: translateY(-50%);
    background: none; border: none; font-size: 14px;
    color: var(--color-text-hint); cursor: pointer; padding: 4px 8px;
    line-height: 1; -webkit-tap-highlight-color: transparent;
  }
  .login-close-btn:hover { color: var(--color-text-secondary); }

  .login-content { display: flex; flex-direction: column; align-items: center; }

  .login-mascot-wrap {
    width: 80px; height: 80px;
    border-radius: 20px;
    overflow: hidden;
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(74,124,89,0.15), 0 1px 4px rgba(0,0,0,0.08);
  }
  .login-mascot-img { width: 100%; height: 100%; object-fit: cover; display: block; }

  .login-title {
    font-size: 19px; font-weight: 500;
    color: var(--color-text-heading);
    margin-bottom: 6px; text-align: center;
  }

  .login-subtitle {
    font-size: 13px; color: var(--color-text-secondary);
    text-align: center; margin-bottom: 24px; line-height: 1.6;
  }

  .login-google-wrap {
    width: 100%; display: flex; justify-content: center; align-items: center;
    gap: 10px; min-height: 40px;
  }

  .login-google-btn { width: 100%; display: flex; justify-content: center; }
  .login-google-btn:empty { display: none; }

  .login-divider {
    width: 100%; display: flex; align-items: center; gap: 12px;
    margin: 18px 0 4px; color: var(--color-text-hint); font-size: 12px;
  }
  .login-divider::before, .login-divider::after {
    content: ''; flex: 1; height: 0.5px; background: var(--color-border);
  }

  .login-form { width: 100%; display: flex; flex-direction: column; gap: 14px; }

  .login-field { display: flex; flex-direction: column; gap: 6px; }

  .login-label { font-size: 13px; font-weight: 500; color: var(--color-text-secondary); }

  .login-input {
    width: 100%; padding: 12px 14px;
    background: var(--color-bg-input);
    border: 0.5px solid var(--color-border);
    border-radius: var(--radius-md);
    color: var(--color-text-heading);
    font-family: inherit; font-size: 16px; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    -webkit-tap-highlight-color: transparent;
  }

  .login-input::placeholder { color: var(--color-text-hint); }
  .login-input:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-focus-ring); }
  .login-input:disabled { opacity: 0.5; }

  .login-error {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px;
    background: rgba(220, 38, 38, 0.06);
    border: 0.5px solid rgba(220, 38, 38, 0.25);
    border-radius: var(--radius-sm);
    font-size: 13px; color: #DC2626;
  }

  .login-cta {
    width: 100%; padding: 0 20px; margin-top: 4px; height: 44px;
    background: var(--color-primary); color: #EAF3DE; border: none;
    border-radius: var(--radius-md); font-family: inherit;
    font-size: 14px; font-weight: 500;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: background 0.15s, transform 0.1s;
    -webkit-tap-highlight-color: transparent;
  }

  .login-cta:hover { background: var(--color-primary-hover); }
  .login-cta:active { transform: scale(0.985); }
  .login-cta:disabled { opacity: 0.4; pointer-events: none; }
  .login-cta__arrow { font-size: 16px; }

  .login-spinner {
    display: inline-block; width: 18px; height: 18px;
    border: 2px solid rgba(234,243,222,0.3); border-top-color: #EAF3DE;
    border-radius: 50%; animation: login-spin 0.7s linear infinite;
  }

  .login-spinner--dark {
    border: 2px solid var(--color-border); border-top-color: var(--color-primary);
  }

  @keyframes login-spin { to { transform: rotate(360deg); } }

  .login-toggle { margin-top: 18px; font-size: 13px; color: var(--color-text-hint); text-align: center; }

  .login-toggle-btn {
    background: none; border: none; color: var(--color-primary);
    font-family: inherit; font-size: 13px; font-weight: 500;
    cursor: pointer; padding: 0; text-decoration: underline;
    text-underline-offset: 2px; -webkit-tap-highlight-color: transparent;
  }

  .login-toggle-btn:disabled { opacity: 0.4; }

  .login-trust { margin-top: 14px; font-size: 12px; color: var(--color-text-hint); text-align: center; }
  .login-privacy { margin-top: 8px; font-size: 11px; color: var(--color-text-hint); text-align: center; padding-bottom: 4px; }
  .login-privacy-link { color: var(--color-text-hint); text-decoration: underline; text-underline-offset: 2px; }
  .login-privacy-link:hover { color: var(--color-primary); }
`;
